import * as React from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useCollaborationBar } from "@/shared/state/collaborationBar";

import { stashAgentBuilderKickoff } from "./agentBuilderKickoff";
import styles from "./CreateAgentMenu.module.css";

interface CreateOption {
  id: "scratch" | "chat";
  label: string;
  description: string;
}

const OPTIONS: CreateOption[] = [
  {
    id: "scratch",
    label: "Start from scratch",
    description:
      "Open the empty agent editor and fill in name, goals, and kickoff message yourself.",
  },
  {
    id: "chat",
    label: "Build with chat",
    description:
      "Have a conversation with the AI to define the agent, then land in the editor with a draft to review.",
  },
];

/**
 * Split create-agent button: one click opens a small popover with two
 * entry-points — the existing empty-form route, and a new "Build with
 * chat" path that hands the user off to the Agent Chat sidebar with a
 * curated kickoff message.
 *
 * Visually mirrors {@link AddGoalMenu} but does NOT claim the WAI-ARIA
 * `menu` role: that role advertises Escape/Arrow/Home/End/type-ahead
 * keyboard semantics, none of which we implement. The popover is a
 * plain click-to-show panel with two buttons — `aria-haspopup="true"`
 * tells assistive tech that activating the trigger opens additional
 * content; Escape-to-close gives keyboard users the one affordance the
 * panel actually needs.
 */
export function CreateAgentMenu() {
  const navigate = useNavigate();
  const collaborationBar = useCollaborationBar();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const mouseHandler = (event: MouseEvent) => {
      if (
        anchorRef.current !== null &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", mouseHandler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", mouseHandler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [open]);

  const handleOptionClick = (option: CreateOption) => {
    setOpen(false);
    if (option.id === "scratch") {
      navigate(Goto.route.CreateAgent);
      return;
    }
    // "chat": stash the curated kickoff and open the Agent Chat sidebar.
    // The sidebar's AgentThread component picks up the kickoff via either its
    // mount-time sessionStorage read (when the bar was closed) or the
    // window event (when the bar is already open).
    stashAgentBuilderKickoff();
    collaborationBar.setActiveMode("ai-agent-chat");
  };

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <Button
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Create agent
      </Button>
      {open && (
        <div className={styles.popover}>
          {OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={styles.option}
              onClick={() => handleOptionClick(option)}
            >
              <span className={styles.optionLabel}>{option.label}</span>
              <span className={styles.optionDescription}>
                {option.description}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
